import React, { useState, useEffect, useRef } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TemporaryMsgAutoCompleteTimeTextField from "./TemporaryMsgAutoCompleteTimeTextField";
import {
  isEndDateTimeValid,
  isStartDateTimeValid,
  isStartAndEndDateTimeValid,
} from "../../../utils";
import dayjs from "../../../../../../utils/dayjs";

interface TimeOption {
  label: string;
  value?: string;
  [key: string]: unknown;
}

interface Props {
  name?: string;
  startDate?: Date;
  endDate?: Date;
  startTimeValue?: string;
  endTimeValue?: string;
  startTimeOptions?: TimeOption[];
  endTimeOptions?: TimeOption[];
  handleStartTimeChange?: (value: string) => void;
  handleEndTimeChange?: (value: string) => void;
  startTimeOption?: string;
  endTimeOption?: string;
  disabled?: boolean;
  row?: Record<string, unknown>;
  timeZone?: string;
}

const TemporaryMsgAutoCompleteTime = ({
  name,
  startDate,
  endDate,
  startTimeValue,
  endTimeValue,
  startTimeOptions,
  endTimeOptions,
  handleStartTimeChange,
  handleEndTimeChange,
  startTimeOption,
  endTimeOption,
  disabled = false,
  row,
  timeZone,
}: Props) => {
  const [startOptions, setStartOptions] = useState<TimeOption[]>([]);
  const [endOptions, setEndOptions] = useState<TimeOption[]>([]);
  const [startInputValue, setStartInputValue] = useState("");
  const [endInputValue, setEndInputValue] = useState("");
  const [startTimeError, setStartTimeError] = useState(false);
  const [startTimeErrorMessage, setStartTimeErrorMessage] = useState("");
  const [endTimeError, setEndTimeError] = useState(false);
  const [endTimeErrorMessage, setEndTimeErrorMessage] = useState("");
  const rowStatus = row?.status;

  useEffect(() => {
    if (startTimeOptions && startTimeOptions.length > 0) {
      setStartOptions(startTimeOptions);
    }
    if (endTimeOptions && endTimeOptions.length > 0) {
      setEndOptions(endTimeOptions);
    }
  }, [startTimeOptions, endTimeOptions]);

  useEffect(() => {
    startTimeValue && setStartInputValue(startTimeValue);
    endTimeValue && setEndInputValue(endTimeValue);

    if (startTimeValue !== startInputValue) {
      setEndTimeErrorMessage("");
      setEndTimeError(false);
    }
  }, [startTimeValue, endTimeValue]);

  useEffect(() => {
    startTimeValue &&
      endTimeValue &&
      handleEndDateAndTimeOnChange(
        startDate,
        endDate,
        startTimeValue,
        endTimeValue,
      );
  }, [startDate, endDate, startTimeValue, endTimeValue, rowStatus, timeZone]);

  const updated = useRef(false);
  useEffect(() => {
    if (updated.current) {
      if (startTimeValue) {
        const isSEValid = isStartAndEndDateTimeValid(
          startDate as unknown as string,
          endDate as unknown as string,
          startTimeValue,
          endTimeValue ?? "",
          startTimeOption ?? "",
          endTimeOption ?? "",
          timeZone,
        );
        if (isSEValid) {
          setStartTimeErrorMessage("");
          setStartTimeError(false);
        } else {
          setStartTimeErrorMessage("Please enter a valid Start time");
          setStartTimeError(true);
        }
        handleEndDateTimeError(
          startDate,
          endDate,
          startTimeValue,
          endTimeValue ?? "",
        );
      }
    }
    if (!updated.current) {
      updated.current = true;
    }
  }, [
    startDate,
    endDate,
    startTimeValue,
    endTimeValue,
    startTimeOption,
    endTimeOption,
    timeZone,
  ]);

  const handleEndDateAndTimeOnChange = (
    startDate: Date | undefined,
    endDate: Date | undefined,
    startTime: string,
    endTime: string,
  ) => {
    handleEndDateTimeError(
      startDate,
      endDate,
      startTimeValue,
      endTime,
      timeZone,
    );

    setEndInputValue(endTime);
  };

  const handleStartTimeOnInputChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ) => {
    handleStartDateTimeError(newValue, startDate, endInputValue);
    setStartInputValue(newValue);
    dayjs(newValue, "hh:mm A").isValid() && handleStartTimeChange?.(newValue);
  };

  const handleStartDateTimeError = (
    newValue: string,
    startDate: Date | undefined,
    endInputValue: string,
  ) => {
    if (isStartDateTimeValid(newValue, startDate, endInputValue, timeZone)) {
      setStartTimeErrorMessage("");
      setStartTimeError(false);
    } else {
      setStartTimeErrorMessage("Please enter a valid Start time");
      setStartTimeError(true);
    }
  };
  const handleEndDateTimeError = (
    startDate: Date | undefined,
    endDate: Date | undefined,
    startTimeValue: string | undefined,
    endTimeValue: string,
    _tz?: string,
  ) => {
    if (
      isEndDateTimeValid(
        startDate,
        endDate,
        startTimeValue ?? "",
        endTimeValue,
        startTimeOption ?? "",
        endTimeOption ?? "",
        timeZone,
      )
    ) {
      setEndTimeErrorMessage("");
      setEndTimeError(false);
    } else {
      setEndTimeErrorMessage("Please enter a valid End time");
      setEndTimeError(true);
    }
  };

  const handleEndTimeOnInputChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ) => {
    handleEndDateAndTimeOnChange(startDate, endDate, startInputValue, newValue);
    handleEndTimeChange?.(newValue);
  };

  return (
    <Autocomplete
      size="small"
      disableClearable
      freeSolo
      value={name === "Start Time" ? startInputValue : endInputValue}
      inputValue={name === "Start Time" ? startInputValue : endInputValue}
      onInputChange={
        name === "Start Time"
          ? handleStartTimeOnInputChange
          : handleEndTimeOnInputChange
      }
      sx={{
        "& .MuiInputBase-input": {
          height: "1rem",
        },
      }}
      disabled={disabled}
      options={
        name === "Start Time"
          ? startOptions && startOptions.map((option) => option.label)
          : endOptions && endOptions.map((option) => option.label)
      }
      renderInput={(params) => (
        <TemporaryMsgAutoCompleteTimeTextField
          isError={name === "Start Time" ? startTimeError : endTimeError}
          errorMessage={
            name === "Start Time" ? startTimeErrorMessage : endTimeErrorMessage
          }
          params={params}
        />
      )}
      ListboxProps={{
        style: {
          maxHeight: 150,
          fontSize: 12,
        },
      }}
    />
  );
};

export default TemporaryMsgAutoCompleteTime;
